import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import { Button } from 'react-bootstrap';
import { CircularProgress } from '@mui/material';
import { uploadFile } from '../../../../shared/services/FileUploadService';
import { saveAttachments } from '../../../../../redux/actions/lenderRequestActions';
import {
  setRemoveDRStep,
  submitRemoveDRRequest,
  setRemoveDRPartialFailure,
  selectSelectedRestrictions,
  selectCurrentRestrictions,
  selectRemoveDRNotes
} from '../../../../../redux/features/lender-request/removeDeliveryRestrictionSlice';

const ReviewAndSubmitPage = ({
  projectId,
  projectInfo,
  formState,
  selectedRestrictions,
  currentRestrictions,
  requestNotes,
  requestTypeId,
  submitRemoveDRRequest,
  saveAttachments,
  setRemoveDRPartialFailure,
  incrementCounter,
  decrementCounter
}) => {
  const [uploads, setUploads] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    // Retrieve uploads from formState
    if (formState && formState.selectedFiles) {
      setUploads(formState.selectedFiles);
    }
  }, [formState]);

  const handleSubmit = async () => {
    // Early validation - prevent submission if requestTypeId is missing
    if (!requestTypeId) {
      console.error('[REMOVE_DR] Cannot submit: requestTypeId is missing');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    let requestCreated = false;
    let requestId = null;

    try {
      const passData = JSON.parse(sessionStorage.getItem('passData'));
      const lenderId = location.hostname === 'localhost'
        ? '12346'
        : sessionStorage.getItem('lenderId') || passData.sellerServicerCoNo;
      
      const payload = {
        requestTypeId: requestTypeId,
        projectId: projectId,
        phaseId: null,
        lenderId: lenderId,
        createdBy: passData.user,
        notes: requestNotes || '',
        restrcnIds: selectedRestrictions.map(r => r.restrcnId),
        reasonCodes: []
      };
      
      // Step 1: Submit the request to get the requestId
      requestId = await submitRemoveDRRequest(payload);
      
      // If requestId is not returned, display an error and throw an exception
      if (!requestId) {
        const errorMessage = "Failed to submit request. Invalid request ID returned.";
        throw new Error(errorMessage);
      }

      // Mark that request was successfully created
      requestCreated = true;

      if (uploads.length > 0) {
        const uploadTasks = uploads.map((fileObj, index) => new Promise((resolve, reject) => {
          const onSuccess = data => resolve({
            documentRankingId: fileObj.docType.documentRankingId,
            docMapId: data?.docMapId,
            note: fileObj.note
          });
          const onFail = error => {
            console.error(`[REMOVE_DR] Upload failed for file ${index}`, { error, fileObj });
            reject(error);
          };

          uploadFile(
            fileObj.file,
            fileObj.note,
            null,
            null,
            projectId,
            fileObj.file.name,
            onSuccess,
            onFail
          ).catch(onFail);
        }));

        const uploadResults = await Promise.allSettled(uploadTasks);
        const failedUploads = uploadResults.filter(result => result.status === 'rejected');
        if (failedUploads.length > 0) {
          throw new Error(`${failedUploads.length} file upload(s) failed`);
        }

        const saved = await saveAttachments(
          requestId,
          uploadResults.map(result => result.value)
        );
        if (!saved) {
          throw new Error('Failed to save attachments to request');
        }
      }

      setRemoveDRPartialFailure(false);
      incrementCounter();
      return;
      
    } catch (error) {
      console.error('[REMOVE_DR] Error submitting request:', error);
      
      // If the request was created but an error occurred during file uploads,
      // treat it as a partial failure and proceed to success page
      if (requestCreated && requestId) {
        console.warn('[REMOVE_DR] Request created but error during file upload phase');
        setRemoveDRPartialFailure(true);
        incrementCounter();
      } else {
        // Request was never created - this is a full failure
        setSubmitError(error.response?.data?.message || error.message || "An error occurred while submitting your request. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Preserve uploads when going back
    const updatedFormState = {
      ...formState,
      selectedFiles: uploads
    };
    decrementCounter(updatedFormState);
  };

  const formatUploadedFiles = () => {
    const fileCount = uploads?.length || 0;
    if (fileCount === 0) return "No files selected";
    return `${fileCount} file${fileCount !== 1 ? 's' : ''} selected for upload`;
  };

  // Check if requestTypeId is missing
  const hasRequestTypeError = !requestTypeId;

  return (
    <Grid item xs={12} sm={12} md={10}>
      <Grid item xs={12} sm={12} md={10}>
        <h1>Review & Submit Request</h1>
      </Grid>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <h6>Step 3 of 4 - Final Review</h6>
        <p>Please review your request details before submitting:</p>
      </Grid>

      {/* Error when requestTypeId is missing */}
      {hasRequestTypeError && (
        <Grid item xs={12} style={{
          background: "#f8d7da",
          color: "#721c24",
          padding: "16px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #f5c6cb"
        }}>
          <strong>Configuration Error:</strong> Unable to load request type information. Please go back and try again, or contact support if the problem persists.
        </Grid>
      )}
      
      <Grid item xs={12} style={{ 
        border: "1px solid #e6e8ed", 
        borderRadius: "8px", 
        padding: "20px", 
        marginBottom: "20px",
        borderLeft: "4px solid #007bff"
      }}>
        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
          Project Information
        </h3>
        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px" }}>Project Name:</span>
          <span>{projectInfo?.projectName}</span>
        </div>
        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px" }}>Current Delivery Restrictions:</span>
          <span>{currentRestrictions?.map(dr => dr.restrictionName || dr.restrcnName).join(', ') || 'None'}</span>
        </div>
      </Grid>

      {/* Selected Restrictions Section */}
      <Grid item xs={12} style={{ 
        border: "1px solid #e6e8ed", 
        borderRadius: "8px", 
        padding: "20px", 
        marginBottom: "20px",
        borderLeft: "4px solid #007bff"
      }}>
        <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
          Request Details
        </h3>

        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px" }}>
            Restrictions to Remove:
          </span>
          <span>{selectedRestrictions?.map(dr => dr.restrictionName || dr.restrcnName).join(', ') || 'None'}</span>
        </div>

        <div style={{ marginBottom: "10px", display: "flex" }}>
          <span style={{ fontWeight: "600", marginRight: "10px" }}>Summary & Analysis:</span>
          <span style={{ whiteSpace: "pre-wrap" }}>{requestNotes}</span>
        </div>
      </Grid>

      {/* Uploaded Documents Section */}
      {uploads.length > 0 && (
        <Grid item xs={12} style={{ 
          border: "1px solid #e6e8ed", 
          borderRadius: "8px", 
          padding: "20px", 
          marginBottom: "20px",
          borderLeft: "4px solid #007bff"
        }}>
          <h3 style={{ marginTop: 0, marginBottom: "15px", color: "#333", fontSize: "18px" }}>
            Attachments
          </h3>
          <div style={{ marginBottom: "10px", display: "flex" }}>
            <span style={{ fontWeight: "600", marginRight: "10px" }}>Selected Files:</span>
            <span>{formatUploadedFiles()}</span>
          </div>
          
          {uploads.map((fileObj, index) => (
            <div key={index} style={{ marginBottom: "10px", display: "flex" }}>
              <span style={{ fontWeight: "600", marginRight: "10px" }}>{fileObj.docType.name}:</span>
              <span style={{ 
                color: "#007bff",
                fontWeight: "bold"
              }}>
                📎 {fileObj.file.name}
              </span>
            </div>
          ))}
        </Grid>
      )}

      {/* Upload Progress Display */}
      {isSubmitting && (
        <Grid item xs={12} style={{
          background: "#d1ecf1",
          color: "#0c5460",
          padding: "16px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #bee5eb",
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <div>
            File upload in progress. This may take a few minutes. Please do not close this window.
          </div>
        </Grid>
      )}

      {/* Error Display */}
      {submitError && (
        <Grid item xs={12} style={{
          background: "#f8d7da",
          color: "#721c24",
          padding: "12px",
          borderRadius: "4px",
          marginBottom: "20px",
          border: "1px solid #f5c6cb"
        }}>
          <strong>Error:</strong> {submitError}
        </Grid>
      )}

      {/* Action Buttons */}
      <Grid container spacing={2} className="dialoguClass">
        <Grid item xs={12} sm={12} md={10}>
          <Grid container justifyContent="space-between">
            <Button
              type="submit"
              className="back-button"
              style={{width: "150px"}}
              onClick={handleBack}
              disabled={isSubmitting}
              form="addProjectForm"
            >
              Back
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || hasRequestTypeError}
              className={"btn-submit" + (isSubmitting ? " btn-loading" : "")}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress
                    size={20}
                    style={{
                      color: "#fff",
                      marginRight: "10px"
                    }}
                  />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>            
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  selectedRestrictions: selectSelectedRestrictions(state),
  currentRestrictions: selectCurrentRestrictions(state),
  requestNotes: selectRemoveDRNotes(state),
  requestTypeId: state.lenderRequestReducer.requestTypeId
});

const mapDispatchToProps = {
  submitRemoveDRRequest,
  saveAttachments,
  setRemoveDRStep,
  setRemoveDRPartialFailure
};

ReviewAndSubmitPage.propTypes = {
  projectId: PropTypes.string.isRequired,
  projectInfo: PropTypes.object,
  formState: PropTypes.object,
  selectedRestrictions: PropTypes.arrayOf(PropTypes.object),
  currentRestrictions: PropTypes.arrayOf(PropTypes.object),
  requestNotes: PropTypes.string,
  requestTypeId: PropTypes.number,
  submitRemoveDRRequest: PropTypes.func.isRequired,
  saveAttachments: PropTypes.func.isRequired,
  setRemoveDRPartialFailure: PropTypes.func.isRequired,
  incrementCounter: PropTypes.func.isRequired,
  decrementCounter: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewAndSubmitPage);

